import { useState } from 'react';
import { AnswersDTO } from '../../api/useRiskToleranceWorkflowQuery';
import { defaultAnswers, OnSubmitParams } from './OrionRiskToleranceWorkflow.meta';
import { useRouteParams } from '../../hooks/useRouteParams';
import { useSubmitAnswersMutation } from '../../api/useSubmitAnswersMutation';
import { useCompleteWorkflowMutation } from '../../api/useCompleteWorkflowMutation';
import { useWorkflowCompleteNavigateAway } from '../../hooks/useWorkflowCompleteNavigateAway';

interface UseRiskToleranceWorkflowParams {
  workflowAnswers: AnswersDTO | null;
  isAutoAccountsWorkflow: boolean;
  onFinish?(): void;
}

export const useOrionRiskToleranceWorkflow = ({
  workflowAnswers,
  onFinish,
  isAutoAccountsWorkflow,
}: UseRiskToleranceWorkflowParams) => {
  const [stepIndex, setStepIndex] = useState(0);

  const [showResults, setShowResults] = useState(false);

  const [answers, setAnswers] = useState<AnswersDTO>(workflowAnswers || defaultAnswers);

  const { clientId } = useRouteParams();

  const submitAnswersMutation = useSubmitAnswersMutation();

  const completeWorkflowMutation = useCompleteWorkflowMutation();

  const { navigateAway, isLoading: isLoadingNavigateAway } = useWorkflowCompleteNavigateAway();

  const submitAnswers = async (_answers: AnswersDTO) => {
    try {
      await submitAnswersMutation.mutateAsync({ clientId, answers: _answers });

      if (isAutoAccountsWorkflow && onFinish) {
        onFinish();

        return;
      }

      setShowResults(true);
    } catch (err) {
      console.error(err);
    }
  };

  const completeWorkflow = async () => {
    try {
      await completeWorkflowMutation.mutateAsync({ clientId });
      await navigateAway();
    } catch (err) {
      console.error(err);
    }
  };

  const onNext = async () => {
    setStepIndex(stepIndex + 1);
  };

  const onPrevious = () => {
    if (stepIndex === 0) {
      window.history.back();
    } else {
      setStepIndex(stepIndex - 1);
    }
  };

  const onSubmit = ({ stepId, answer }: OnSubmitParams) => {
    setAnswers({ ...answers, [stepId]: answer });
  };

  return {
    state: {
      answers,
      stepIndex,
      showResults,
      setStepIndex,
      setShowResults,
      isLoadingNavigateAway,
    },

    actions: {
      onNext,
      onPrevious,
      onSubmit,
      submitAnswers,
      completeWorkflow,
    },

    mutations: {
      submitAnswersMutation,
      completeWorkflowMutation,
    },
  };
};
